import { FC } from "react";
import { Checkbox, Hide } from "shared/components";
import { Switch } from "shared/components/forms/Switch";
import { ApplicantType, VerificationNames } from "shared/types";
import { useTemplateContext } from "../provider";
import SuccessIcon from "shared/icons/status-icons/success-icon.svg?react";
import FailIcon from "shared/icons/status-icons/fail-icon.svg?react";
import { CheckComponent } from "../components/CheckComponent";
import { ConfigurableCard } from "../components/ConfigurableCard";

type Props = {
  toggleServiceEnabled: (v?: boolean) => void;
  toggleServiceRequired: () => void;
};

const mapLeadSourceToTitle = {
  "Referral (Current Resident)": "Referral (Current resident)",
  "Referral (Previous Resident)": "Referral (Previous resident)",
  "Apartments.com": "Apartments.com",
  "Multiple Listing Service (MLS)": "Multiple Listing Service (MLS)",
  Zillow: "Zillow",
  "Walk-in / site signage": "Walk-in / site signage",
  Google: "Google",
  Facebook: "Facebook",
  Instagram: "Instagram",
  Craigslist: "Craigslist",
  "QR Code": "QR Code",
  Other: "Other"
};

export const LeadSourceView: FC<Props> = ({
  toggleServiceEnabled,
  toggleServiceRequired
}) => {
  const {
    readOnly,
    partnerType,
    template,
    combinedVerificationSchemas,
    selectedStep
  } = useTemplateContext();
  const askCustomer =
      template?.verification_schemas[partnerType][
        VerificationNames.RentalApplication
      ]?.[`lead_source_step_enabled`],
    details =
      combinedVerificationSchemas[selectedStep].json_schema.properties?.[
        "lead_source"
      ]?.enum ||
      combinedVerificationSchemas[selectedStep].json_schema.properties?.[
        "lead_source"
      ]?.anyOf ||
      [],
    answerRequired =
      template?.verification_schemas[partnerType][
        VerificationNames.RentalApplication
      ]?.[`lead_source_step_required`];

  const customer =
    partnerType === ApplicantType.CoSigner ? "guarantor" : "applicant";
  const askCustomerV = askCustomer ?? true;
  return (
    <ConfigurableCard title="Lead source">
      <div className="flex justify-between items-center">
        {readOnly ? (
          <div className="flex gap-2 items-center">
            {askCustomer ? (
              <SuccessIcon className="text-primary-500 w-5" />
            ) : (
              <FailIcon className="text-content-100 w-5" />
            )}
            <span className="text-sm font-medium text-content-1000 ">
              {`${askCustomer ? "Ask" : "Don't ask"} the ${customer} how they heard about the property`}
            </span>
          </div>
        ) : (
          <Switch
            className="py-2"
            checked={askCustomerV}
            label={
              <p className="text-sm font-medium">
                {`Ask the ${customer} how they heard about the property`}
              </p>
            }
            onChange={() => toggleServiceEnabled(!askCustomerV)}
          />
        )}
        <div>
          {readOnly ? (
            <CheckComponent
              checked={Boolean(answerRequired)}
              label="Answer required"
            />
          ) : (
            <Checkbox
              checked={Boolean(answerRequired)}
              label="Answer required"
              onChange={() => toggleServiceRequired()}
              disabled={!askCustomerV}
            />
          )}
        </div>
      </div>
      <Hide if={readOnly && !askCustomer}>
        <div className="mt-4 border-t border-alpha-40 pt-4">
          <p className="text-sm text-content-400 font-medium mb-4">
            <span className="capitalize">{customer}</span> can select one of
            these options:{" "}
          </p>
          {details.map((source, index) => {
            if (typeof source === "object" && source.enum[0] === "")
              return null;

            const title = typeof source === "object" ? source.enum[0] : source;

            return (
              <div
                key={index}
                className="mb-2 rounded-lg border border-alpha-80 bg-alpha-40 px-4 py-3"
              >
                <p className="text-sm font-medium text-content-500">
                  {mapLeadSourceToTitle[title] || title}
                </p>
              </div>
            );
          })}
        </div>
      </Hide>
    </ConfigurableCard>
  );
};
